// this component use to dynamically render the element

import produce from "immer";
import React, { FC, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  startEditElement,
  startMoveElement,
  startMoveElementSection,
} from "../../actions/elementListAction";
import { startEditSession } from "../../actions/sessionListAction";
import { Element } from "../../models/elementModel";
import { getSession } from "../../selectors/appSelectors";
import { SystemTypes } from "../../utils/constants";
import { ItemTypes } from "../../utils/itemTypes";
import InputElement from "./renderElementComponents/inputElement/InputElement";
import CommonPropertyElement from "./CommonPropertyElement";
import TextAreaElement from "./renderElementComponents/TextAreaElement";
import ActionListComponent from "../utilComponents/actionListComponent/actionListComponent";

export interface RenderElementProps {
  index: number;
  element: Element;
  key: string;
  isPreview?: boolean;
}

const RenderElement: FC<RenderElementProps> = ({
  index,
  element,
  key,
  isPreview = false,
}) => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const sessionState = useSelector(getSession);

  const selectElementAction = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    const newSession = produce(sessionState, (draft) => {
      draft.selectedElementId = element.id;
      draft.selectedType = SystemTypes.ELEMENT;
    });
    dispatch(startEditSession(newSession));
  };

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ELEMENT_MOVE,
    item: { dragItem: element, index: index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.ELEMENT_MOVE,
    drop(item: any, monitor) {
      const itemType = monitor.getItemType();
      if (itemType === ItemTypes.ELEMENT_MOVE) {
        if (item.dragItem.sectionId !== element.sectionId) {
          const didDrop = monitor.didDrop();
          if (didDrop) {
            return;
          }
          dispatch(startMoveElementSection(item.dragItem, element));
        }
      }
    },
    hover(item: any, monitor) {
      // item is the dragged element
      if (!ref.current) {
        return;
      }

      if (item.dragItem.sectionId === element.sectionId) {
        const dragIndex = item.index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        // Time to actually perform the action
        dispatch(startMoveElement(item.dragItem, element));

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex;
      }
    },
  });

  drop(drag(ref));

  return (
    <>
      {!isPreview && (
        <div
          key={index}
          ref={ref}
          onClick={(e) => {
            selectElementAction(e);
          }}
        >
          <CommonPropertyElement index={key} element={element}/>
          {sessionState.selectedType === SystemTypes.ELEMENT &&
            sessionState.selectedElementId === element.id && (
              <div key={index} className="action-list-panel">
                <ActionListComponent type="element" element={element} />
              </div>
            )}
        </div>
      )}
      {isPreview && (
        <div key={index}>
          <CommonPropertyElement index={key} element={element} isPreview={isPreview}/>
        </div>
      )}
    </>
  );
};

export default RenderElement;
